import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Settings, Save, RotateCcw, AlertTriangle, CheckCircle, XCircle, Info, Loader2 } from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Switch } from '../../../../components/ui/switch';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Progress } from '../../../../components/ui/progress';
import { Badge } from '../../../../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status Badge Component
const StatusBadge = ({ status, children }) => {
  const variants = {
    active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    disabled: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    warning: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    error: 'bg-red-500/10 text-red-400 border-red-500/20',
    info: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  };
  return (
    <Badge variant="outline" className={`${variants[status] || variants.info} text-xs`}>
      {children}
    </Badge>
  );
};

// Progress Bar with Label
const UsageProgressBar = ({ label, current, limit, className = '' }) => {
  const percent = limit > 0 ? (current / limit) * 100 : 0;
  const isNearLimit = percent >= 80;
  const isAtLimit = percent >= 100;
  
  return (
    <div className={className}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-zinc-400">{label}</span>
        <span className={`text-sm font-mono ${isAtLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : 'text-zinc-300'}`}>
          {current.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
      <Progress 
        value={Math.min(percent, 100)} 
        className={`h-2 ${isAtLimit ? '[&>div]:bg-red-500' : isNearLimit ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`}
      />
    </div>
  );
};

/**
 * Google Address Services Integration Settings
 * Located at: Platform Settings → Integrations → Google Address Services
 */
export default function GoogleAddressSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(null);

  // Fetch settings and usage
  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/platform-settings/google-address`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (data.success) {
        setSettings(data.settings);
        setUsage(data.usage);
        setOriginalSettings(JSON.stringify(data.settings));
      } else {
        toast.error(data.detail || 'Failed to load settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load Google Address settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Track changes
  useEffect(() => {
    if (settings && originalSettings) {
      setHasChanges(JSON.stringify(settings) !== originalSettings);
    }
  }, [settings, originalSettings]);

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/platform-settings/google-address`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });
      const data = await response.json();
      
      if (data.success) {
        setSettings(data.settings);
        setUsage(data.usage);
        setOriginalSettings(JSON.stringify(data.settings));
        setHasChanges(false);
        toast.success('Settings saved successfully');
      } else {
        toast.error(data.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleResetDefaults = async () => {
    if (!window.confirm('Reset all Google Address settings to defaults? Current usage counters will NOT be reset.')) {
      return;
    }
    
    setResetting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/platform-settings/google-address/reset-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (data.success) {
        setSettings(data.settings);
        setUsage(data.usage);
        setOriginalSettings(JSON.stringify(data.settings));
        setHasChanges(false);
        toast.success('Settings reset to defaults');
      } else {
        toast.error(data.detail || 'Failed to reset settings');
      }
    } catch (error) {
      console.error('Error resetting settings:', error);
      toast.error('Failed to reset settings');
    } finally {
      setResetting(false);
    }
  };

  // Update setting field
  const updateSetting = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1F18] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const apiKeyConfigured = usage?.api_key_configured;
  const servicesEnabled = settings?.google_address_services_enabled && apiKeyConfigured;

  return (
    <div className="min-h-screen bg-[#0B1F18] text-white p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <MapPin className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Google Address Services</h1>
              <p className="text-[#93AE9F] text-sm">Configure address autocomplete and validation API</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleResetDefaults}
              disabled={resetting || saving}
              className="border-zinc-700 hover:bg-zinc-800 text-zinc-300"
            >
              {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Reset Defaults
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Status Card */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Service Status</CardTitle>
              <div className="flex items-center gap-2">
                {!apiKeyConfigured && (
                  <StatusBadge status="error">Needs API Key</StatusBadge>
                )}
                {apiKeyConfigured && !settings?.google_address_services_enabled && (
                  <StatusBadge status="disabled">Disabled</StatusBadge>
                )}
                {servicesEnabled && (
                  <StatusBadge status="active">Active</StatusBadge>
                )}
                {usage?.near_limit && (
                  <StatusBadge status="warning">Near Limit</StatusBadge>
                )}
                {usage?.limit_reached && (
                  <StatusBadge status="error">Limit Reached</StatusBadge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* API Key Warning */}
            {!apiKeyConfigured && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3 mb-6">
                <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-300 font-medium">GOOGLE_MAPS_API_KEY is not configured</p>
                  <p className="text-red-400/80 text-sm mt-1">
                    Add GOOGLE_MAPS_API_KEY to the backend environment to enable Google Address Services.
                  </p>
                </div>
              </div>
            )}

            {/* Service Toggles */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-medium">Google Address Services</Label>
                  <p className="text-zinc-500 text-sm">Master switch for all Google services</p>
                </div>
                <Switch
                  checked={settings?.google_address_services_enabled || false}
                  onCheckedChange={(checked) => updateSetting('google_address_services_enabled', checked)}
                  disabled={!apiKeyConfigured}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-medium">Address Autocomplete</Label>
                  <p className="text-zinc-500 text-sm">Enable address suggestions</p>
                </div>
                <Switch
                  checked={settings?.autocomplete_enabled || false}
                  onCheckedChange={(checked) => updateSetting('autocomplete_enabled', checked)}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-medium">Address Verification</Label>
                  <p className="text-zinc-500 text-sm">Enable address validation</p>
                </div>
                <Switch
                  checked={settings?.validation_enabled || false}
                  onCheckedChange={(checked) => updateSetting('validation_enabled', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Limits Configuration */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Monthly Usage Limits</CardTitle>
            <CardDescription className="text-zinc-400">Configure API call limits</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-zinc-300 text-sm">Platform Monthly Limit</Label>
                <Input
                  type="number"
                  min="0"
                  max="1000000"
                  value={settings?.monthly_platform_limit || 0}
                  onChange={(e) => updateSetting('monthly_platform_limit', parseInt(e.target.value) || 0)}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Autocomplete Limit</Label>
                <Input
                  type="number"
                  min="0"
                  max="1000000"
                  value={settings?.monthly_autocomplete_limit || 0}
                  onChange={(e) => updateSetting('monthly_autocomplete_limit', parseInt(e.target.value) || 0)}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Validation Limit</Label>
                <Input
                  type="number"
                  min="0"
                  max="1000000"
                  value={settings?.monthly_validation_limit || 0}
                  onChange={(e) => updateSetting('monthly_validation_limit', parseInt(e.target.value) || 0)}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Per-Agency Limit</Label>
                <Input
                  type="number"
                  min="0"
                  max="1000000"
                  placeholder="No limit"
                  value={settings?.per_agency_monthly_limit || ''}
                  onChange={(e) => updateSetting('per_agency_monthly_limit', e.target.value ? parseInt(e.target.value) : null)}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Protection & Cache Settings */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Protection Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-300 text-sm">Warning Threshold (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings?.warning_threshold_percent || 80}
                  onChange={(e) => updateSetting('warning_threshold_percent', parseInt(e.target.value) || 80)}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-medium">Hard Stop at Limit</Label>
                  <p className="text-zinc-500 text-sm">Block requests when limit reached</p>
                </div>
                <Switch
                  checked={settings?.hard_stop_at_limit !== false}
                  onCheckedChange={(checked) => updateSetting('hard_stop_at_limit', checked)}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-medium">Manual Fallback</Label>
                  <p className="text-zinc-500 text-sm">Allow manual entry when blocked</p>
                </div>
                <Switch
                  checked={settings?.manual_fallback_enabled !== false}
                  onCheckedChange={(checked) => updateSetting('manual_fallback_enabled', checked)}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Cache Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                <div>
                  <Label className="text-white font-medium">Enable Caching</Label>
                  <p className="text-zinc-500 text-sm">Cache results to reduce API calls</p>
                </div>
                <Switch
                  checked={settings?.cache_enabled !== false}
                  onCheckedChange={(checked) => updateSetting('cache_enabled', checked)}
                />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Autocomplete Cache TTL (hours)</Label>
                <Input
                  type="number"
                  min="1"
                  max="168"
                  value={settings?.autocomplete_cache_ttl_hours || 24}
                  onChange={(e) => updateSetting('autocomplete_cache_ttl_hours', parseInt(e.target.value) || 24)}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Validation Cache TTL (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={settings?.validation_cache_ttl_days || 30}
                  onChange={(e) => updateSetting('validation_cache_ttl_days', parseInt(e.target.value) || 30)}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Usage Dashboard */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Usage Dashboard — {usage?.month_key || 'Current Month'}</CardTitle>
            <CardDescription className="text-zinc-400">
              Provider calls, cache savings, and usage analytics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Usage Progress Bars */}
            <div className="space-y-4">
              <UsageProgressBar 
                label="Platform Total Usage"
                current={usage?.provider_calls_used || 0}
                limit={usage?.platform_limit || 1000}
              />
              <UsageProgressBar 
                label="Autocomplete Calls"
                current={usage?.autocomplete_calls || 0}
                limit={usage?.autocomplete_limit || 700}
              />
              <UsageProgressBar 
                label="Validation Calls"
                current={usage?.validation_calls || 0}
                limit={usage?.validation_limit || 300}
              />
            </div>

            {/* Usage Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Provider Calls</p>
                <p className="text-2xl font-semibold text-white mt-1">
                  {(usage?.provider_calls_used || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Remaining</p>
                <p className="text-2xl font-semibold text-emerald-400 mt-1">
                  {(usage?.remaining_provider_calls || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Cache Hits</p>
                <p className="text-2xl font-semibold text-blue-400 mt-1">
                  {(usage?.cache_hits || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Calls Saved</p>
                <p className="text-2xl font-semibold text-emerald-400 mt-1">
                  {(usage?.estimated_calls_saved || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Blocked Calls</p>
                <p className="text-2xl font-semibold text-amber-400 mt-1">
                  {(usage?.blocked_calls || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Provider Errors</p>
                <p className="text-2xl font-semibold text-red-400 mt-1">
                  {(usage?.provider_error_calls || 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Status and Reset Info */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-zinc-800">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 text-sm">Usage:</span>
                  <span className={`text-sm font-medium ${
                    (usage?.usage_percent || 0) >= 100 ? 'text-red-400' : 
                    (usage?.usage_percent || 0) >= 80 ? 'text-amber-400' : 'text-emerald-400'
                  }`}>
                    {(usage?.usage_percent || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {usage?.api_key_configured ? (
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-400" />
                  )}
                  <span className="text-zinc-400 text-sm">
                    API Key {usage?.api_key_configured ? 'Configured' : 'Missing'}
                  </span>
                </div>
              </div>
              <div className="text-zinc-500 text-sm">
                Resets: <span className="text-zinc-300">{usage?.reset_date ? new Date(usage.reset_date).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Note */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-blue-300 text-sm">
              FundElite usage limits control calls inside this SaaS. For billing protection, also configure 
              Google Cloud quota limits and billing alerts in Google Cloud Console.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
